import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CampaignsService } from '../../../../../../services/api/generated/campaigns/campaigns.service';
import type { CampaignCreateSchema, CampaignCreateWithPlanning } from '../../../../../../services/api/generated/model';
import { AssetStore } from '../../../../../../stores/asset.store';
import { CampaignStore } from '../../../../../../stores/campaign.store';
import { ManualMeasureStore } from '../../../../../../stores/manual-measure.store';
import { Campaign, CampaignStatus } from '../../../../../../types/campaign.interface';
import { OPEN_CAMPAIGN_STATUSES } from '../../../../../../types/campaign.utils';
import { Measure } from '../../../../../../types/measure.interface';
import { CampaignCompletionRefreshService } from './campaign-completion-refresh.service';

export type CreateWithPlanningPayload = CampaignCreateSchema;

@Injectable({
  providedIn: 'root',
})
export class CampaignDataService {
  private readonly campaignStore = inject(CampaignStore);
  private readonly campaignsApi = inject(CampaignsService);
  private readonly assetStore = inject(AssetStore);
  readonly manualMeasureStore = inject(ManualMeasureStore);
  private readonly completionRefreshService = inject(CampaignCompletionRefreshService);

  private readonly _currentCampaignId = signal<number | null>(null);
  private readonly _currentAssetId = signal<number | null>(null);

  readonly currentCampaignId = this._currentCampaignId.asReadonly();
  readonly currentAssetId = this._currentAssetId.asReadonly();

  readonly currentCampaign = computed(() => {
    const campaignId = this._currentCampaignId();
    if (!campaignId) return null;
    return this.campaignStore.byId()(campaignId);
  });

  readonly currentAsset = computed(() => {
    const assetId = this._currentAssetId();
    if (!assetId) return null;
    return this.assetStore.byId()(assetId);
  });

  readonly currentAssetName = computed(() => {
    const asset = this.currentAsset();
    return asset ? asset.name : '';
  });

  readonly campaigns = computed(() => this.campaignStore.entities());

  readonly campaignsByAsset = computed(() => {
    const assetId = this._currentAssetId();
    if (!assetId) return [];
    return this.campaignStore.entities().filter((campaign: Campaign) => campaign.assetId === assetId);
  });

  readonly currentOpenCampaigns = computed(() => {
    return this.campaignsByAsset().filter((campaign: Campaign) => OPEN_CAMPAIGN_STATUSES.includes(campaign.status));
  });

  readonly currentCompletedCampaigns = computed(() => {
    return this.campaignsByAsset().filter((campaign: Campaign) => campaign.status === CampaignStatus.DONE);
  });

  readonly currentTotalCampaignsCount = computed(() => {
    return this.campaignsByAsset().length;
  });

  readonly currentAvailableMeasures = computed(() => {
    const assetId = this.currentAssetId();
    if (!assetId) return [];
    return this.manualMeasureStore.entities().filter((measure: Measure) => measure.assetId === assetId);
  });

  setCurrentCampaignId(campaignId: number | null): void {
    this._currentCampaignId.set(campaignId);
  }

  setCurrentAssetId(assetId: number | null): void {
    this._currentAssetId.set(assetId);
  }

  async loadCampaignsByAsset(assetId: number): Promise<Campaign[]> {
    this.setCurrentAssetId(assetId);
    return this.campaignStore.getByAssetId(assetId);
  }

  async loadCampaign(campaignId: number): Promise<Campaign> {
    this.setCurrentCampaignId(campaignId);
    return this.campaignStore.loadById(campaignId);
  }

  async updateCampaign(campaignId: number, updates: Partial<Campaign>): Promise<Campaign> {
    const updated = await this.campaignStore.update(campaignId, updates);
    if (!updated) throw new Error(`Campaign ${campaignId} update produced no payload`);
    return updated;
  }

  async updateStatus(campaignId: number, status: Campaign['status']): Promise<Campaign> {
    const updated = await this.campaignStore.update(campaignId, { status });
    if (!updated) throw new Error(`Campaign ${campaignId} status update produced no payload`);
    return updated;
  }

  async deleteCampaign(campaignId: number): Promise<void> {
    await this.campaignStore.delete(campaignId);

    const assetId = this._currentAssetId();
    if (assetId) {
      this.completionRefreshService.refreshCompletionForCampaign(campaignId).catch(() => {});
    }
  }

  async createCampaign(campaignData: Omit<Campaign, 'id'>, modelId?: number): Promise<Campaign> {
    const logTexts = {
      comment: 'history.comments.creation',
    };

    const payload: CreateWithPlanningPayload = {
      assetId: campaignData.assetId,
      timestamp: campaignData.timestamp,
      status: campaignData.status,
      ...(campaignData.operator !== undefined && { operator: campaignData.operator }),
      ...(campaignData.comment !== undefined && { comment: campaignData.comment }),
      ...(modelId !== undefined && { modelId }),
      logTexts,
    };

    const response = await firstValueFrom(this.campaignsApi.postCampaignListCreateResource<CampaignCreateWithPlanning>(payload));
    const created = response.campaign as Campaign;

    this.campaignStore.addEntity(created);

    this.completionRefreshService.refreshCompletionForCampaign(created.id).catch(() => {});

    return created;
  }
}
